import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, ArrowLeft, ArrowUpRight, BrainCircuit, Cpu, Radar, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { getErrorMessage } from '../lib/utils';
import { aiCommandAPI } from '../services/api';
import { StatSkeletonGrid } from '../components/common/States';

const LIGHT_PANEL = 'rounded-3xl border border-border bg-card text-foreground shadow-[0_18px_40px_rgba(15,23,42,0.06)]';
const LIGHT_SUBPANEL = 'rounded-2xl border border-border bg-muted';
const LIGHT_LABEL = 'text-[11px] uppercase tracking-[0.18em] text-muted-foreground';

const formatINR = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const slugify = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    if (typeof value[0] !== 'object') return value.join(', ');
    return `${value.length} items`;
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toLocaleString('en-IN') : value.toFixed(1);
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const parseDetailRoute = (activeTab) => {
  const parts = String(activeTab || '').split('/').filter(Boolean);
  if (parts.length < 4 || parts[0] !== 'ai-command' || parts[1] !== 'detail') {
    return { section: null, itemId: null };
  }
  return {
    section: decodeURIComponent(parts[2] || ''),
    itemId: decodeURIComponent(parts.slice(3).join('/') || ''),
  };
};

const detailCardItems = (items, mapper) => (items || []).map((item, index) => mapper(item, index)).filter(Boolean);

const buildDetail = (data, activeTab) => {
  const { section, itemId } = parseDetailRoute(activeTab);
  if (!section || !itemId) {
    return null;
  }

  const governanceCenter = data.ai_governance_center || {};
  const promptActivityLogs = governanceCenter.prompt_activity_logs || [];
  const recommendationApprovals = governanceCenter.recommendation_approvals || [];
  const modelHealth = governanceCenter.model_health || [];
  const microservices = governanceCenter.microservices || [];

  if (section === 'summary') {
    const item = (data.summary_strip || []).find((entry) => entry.key === itemId);
    if (!item) return null;
    return {
      eyebrow: 'AI Dashboard Summary',
      title: item.label,
      description: item.delta || 'Executive summary signal from the AI Dashboard.',
      metrics: [
        { label: 'Value', value: item.format === 'currency' ? formatINR(item.value) : formatValue(item.value) },
        { label: 'Tone', value: item.tone || 'primary' },
        { label: 'Format', value: item.format || 'default' },
      ],
      details: [
        { label: 'Key', value: item.key },
        { label: 'Delta', value: item.delta },
      ],
    };
  }

  if (section === 'governance-hero') {
    return {
      eyebrow: 'AI Governance',
      title: 'AI Governance Overview',
      description: 'Centralized visibility for policy enforcement, explainability, approvals, and model operations.',
      metrics: [
        { label: 'Models', value: modelHealth.length },
        { label: 'Approvals', value: recommendationApprovals.length },
        { label: 'Activity Events', value: promptActivityLogs.length },
        { label: 'Services', value: microservices.length },
      ],
      related: detailCardItems(modelHealth, (row) => ({
        key: row.name,
        title: row.name,
        subtitle: `Latency ${row.latency_ms} ms`,
        meta: `${Math.round((row.confidence || 0) * 100)}% confidence • ${row.status}`,
      })),
    };
  }

  if (section === 'prompt') {
    const promptLanes = {
      'tenant-risk-watch': {
        title: 'Tenant Risk Watch',
        description: 'Tenant comparison view for attrition pressure, compliance drag, and AI risk.',
        metrics: [
          { label: 'Tenants', value: (data.tenant_health_matrix || []).length },
          { label: 'Heatmap Signals', value: (data.workforce_risk_heatmap || []).length },
        ],
        related: detailCardItems(data.tenant_health_matrix, (row) => ({
          key: row.tenant_id,
          title: row.tenant_name,
          subtitle: `${row.region} • ${row.status}`,
          meta: `Risk ${row.ai_risk_score} • Attrition ${row.attrition_risk}% • Compliance ${row.compliance_health}`,
        })),
      },
      'payroll-exposure-scan': {
        title: 'Payroll Exposure Scan',
        description: 'AI-driven payroll pressure, anomaly surfacing, and financial movement signals.',
        metrics: [
          { label: 'Alerts', value: (data.ai_anomaly_tracker || []).length },
          { label: 'Forecast Points', value: (data.predictive_analytics?.payroll_forecasting || []).length },
        ],
        related: detailCardItems(data.ai_anomaly_tracker, (row) => ({
          key: row.id,
          title: row.title,
          subtitle: row.tenant_name || row.description,
          meta: `Severity ${row.severity} • Risk ${Number(row.risk_score || 0).toFixed(1)} • ${row.status}`,
        })),
      },
      'governance-readiness': {
        title: 'Governance Readiness',
        description: 'Model health, approvals, and governance activity readiness across the AI platform.',
        metrics: [
          { label: 'Models', value: modelHealth.length },
          { label: 'Approvals', value: recommendationApprovals.length },
          { label: 'Prompt Logs', value: promptActivityLogs.length },
        ],
        related: detailCardItems(recommendationApprovals, (row) => ({
          key: row.id,
          title: row.title,
          subtitle: row.rationale || row.summary,
          meta: `Status ${String(row.status || '').replace('_', ' ')} • Impact ${Number(row.impact_score || 0).toFixed(1)}`,
        })),
      },
    };
    return promptLanes[itemId] || null;
  }

  if (section === 'section') {
    const sectionMap = {
      'copilot-workbench': {
        eyebrow: 'AI Dashboard',
        title: 'Ask AI For Platform Analytics',
        description: 'Cross-tenant copilot workspace for workforce intelligence, payroll exposure, and compliance posture.',
        metrics: [
          { label: 'Prompt Lanes', value: 3 },
          { label: 'Suggestions', value: 4 },
        ],
        related: [
          { key: 's1', title: 'Highest attrition risk', subtitle: 'Which tenants have the highest attrition risk?', meta: 'Copilot suggestion' },
          { key: 's2', title: 'Finance exposure', subtitle: 'Summarize payroll and finance exposure across tenants.', meta: 'Copilot suggestion' },
          { key: 's3', title: 'Compliance comparison', subtitle: 'Compare tenant compliance health and governance coverage.', meta: 'Copilot suggestion' },
          { key: 's4', title: 'Hiring benchmark', subtitle: 'Benchmark tenants by hiring efficiency and AI risk.', meta: 'Copilot suggestion' },
        ],
      },
      'global-ai-insights': {
        eyebrow: 'AI Dashboard',
        title: 'Global AI Insights',
        description: 'Cross-tenant signals synthesized into executive actions.',
        metrics: [{ label: 'Insights', value: (data.global_ai_insights || []).length }],
        related: detailCardItems(data.global_ai_insights, (insight) => ({
          key: insight.id,
          title: insight.title,
          subtitle: insight.summary,
          meta: `${insight.severity || 'medium'} severity • Risk ${Number(insight.risk_score || 0).toFixed(1)}`,
        })),
      },
      'multi-tenant-monitoring': {
        eyebrow: 'AI Dashboard',
        title: 'Multi-Tenant Monitoring',
        description: 'Tenant health matrix, risk benchmarking, and workload comparison.',
        metrics: [
          { label: 'Tenants', value: (data.tenant_health_matrix || []).length },
          { label: 'Heatmap Cells', value: (data.workforce_risk_heatmap || []).length },
        ],
        related: detailCardItems(data.tenant_health_matrix, (row) => ({
          key: row.tenant_id,
          title: row.tenant_name,
          subtitle: `${row.region} • ${row.active_workforce} workforce • ${row.status}`,
          meta: `Risk ${row.ai_risk_score} • Attrition ${row.attrition_risk}% • Payroll ${formatINR(row.payroll_volume)}`,
        })),
      },
      'alerts-center': {
        eyebrow: 'AI Dashboard',
        title: 'AI Alerts Center',
        description: 'Payroll fraud, compliance drift, suspicious activity, and anomaly pressure.',
        metrics: [{ label: 'Alerts', value: (data.ai_anomaly_tracker || []).length }],
        related: detailCardItems(data.ai_anomaly_tracker, (row) => ({
          key: row.id,
          title: row.title,
          subtitle: row.tenant_name || row.description,
          meta: `${row.severity} severity • Risk ${Number(row.risk_score || 0).toFixed(1)} • ${row.status}`,
        })),
      },
      'executive-reports': {
        eyebrow: 'AI Dashboard',
        title: 'Executive AI Reports',
        description: 'AI-generated briefings for platform leadership.',
        metrics: [{ label: 'Reports', value: (data.ai_executive_reports || []).length }],
        related: detailCardItems(data.ai_executive_reports, (row) => ({
          key: row.title,
          title: row.title,
          subtitle: row.summary,
          meta: row.tone,
        })),
      },
      'governance-center': {
        eyebrow: 'AI Dashboard',
        title: 'AI Governance Center',
        description: 'Model health, prompt activity, decision confidence, and policy controls.',
        metrics: [
          { label: 'Models', value: modelHealth.length },
          { label: 'Approvals', value: recommendationApprovals.length },
        ],
        related: detailCardItems(modelHealth, (row) => ({
          key: row.name,
          title: row.name,
          subtitle: `${row.status} • ${row.latency_ms} ms`,
          meta: `${Math.round((row.confidence || 0) * 100)}% confidence • ${row.throughput} throughput`,
        })),
      },
      'activity-timeline': {
        eyebrow: 'AI Dashboard',
        title: 'AI Activity Timeline',
        description: 'Prompt operations, governance audits, and explainability events.',
        metrics: [{ label: 'Events', value: promptActivityLogs.length }],
        related: detailCardItems(promptActivityLogs, (row) => ({
          key: row.id,
          title: row.prompt_text,
          subtitle: `${row.intent} • ${row.actor_role}`,
          meta: `${row.latency_ms} ms • ${row.confidence_score}% confidence`,
        })),
      },
      'service-mesh': {
        eyebrow: 'AI Dashboard',
        title: 'AI Service Mesh',
        description: 'Microservices architecture and operational status.',
        metrics: [{ label: 'Services', value: microservices.length }],
        related: detailCardItems(microservices, (row) => ({
          key: row.name,
          title: row.name,
          subtitle: `${row.runtime} • ${row.mode}`,
          meta: `${row.status} • ${row.purpose}`,
        })),
      },
      'risk-distribution': {
        eyebrow: 'AI Dashboard',
        title: 'Cross-Tenant Risk Distribution',
        description: 'Relative AI risk position across all managed tenants.',
        metrics: [{ label: 'Tenants', value: (data.tenant_health_matrix || []).length }],
        related: detailCardItems(data.tenant_health_matrix, (row) => ({
          key: row.tenant_id,
          title: row.tenant_name,
          subtitle: `${row.region} • ${row.department_focus}`,
          meta: `Risk ${row.ai_risk_score} • Alerts ${row.alert_count}`,
        })),
      },
    };
    return sectionMap[itemId] || null;
  }

  if (section === 'insight') {
    const insight = (data.global_ai_insights || []).find((entry) => String(entry.id) === itemId);
    if (!insight) return null;
    return {
      eyebrow: 'Global AI Insight',
      title: insight.title,
      description: insight.summary,
      metrics: [
        { label: 'Risk Score', value: Number(insight.risk_score || 0).toFixed(1) },
        { label: 'Confidence', value: `${insight.confidence_score || 0}%` },
        { label: 'Severity', value: insight.severity || 'medium' },
      ],
      details: [
        { label: 'Type', value: insight.insight_type },
        { label: 'Region', value: insight.region },
        { label: 'Department', value: insight.department },
      ],
    };
  }

  if (section === 'tenant' || section === 'risk-distribution') {
    const tenant = (data.tenant_health_matrix || []).find((row) => String(row.tenant_id) === itemId);
    if (!tenant) return null;
    const relatedAlerts = (data.ai_anomaly_tracker || []).filter((row) => row.tenant_name === tenant.tenant_name);
    return {
      eyebrow: 'Tenant Detail',
      title: tenant.tenant_name,
      description: `${tenant.region} tenant view with workforce, risk, and monitoring detail.`,
      metrics: [
        { label: 'AI Risk', value: tenant.ai_risk_score },
        { label: 'Attrition', value: `${tenant.attrition_risk}%` },
        { label: 'Workforce', value: tenant.active_workforce },
        { label: 'Payroll', value: formatINR(tenant.payroll_volume) },
      ],
      details: [
        { label: 'Status', value: tenant.status },
        { label: 'Compliance', value: tenant.compliance_health },
        { label: 'Stability', value: tenant.workforce_stability },
        { label: 'Hiring Efficiency', value: tenant.hiring_efficiency },
        { label: 'Department Focus', value: tenant.department_focus },
        { label: 'Alerts', value: tenant.alert_count },
      ],
      related: detailCardItems(relatedAlerts, (row) => ({
        key: row.id,
        title: row.title,
        subtitle: row.description,
        meta: `${row.severity} severity • Risk ${Number(row.risk_score || 0).toFixed(1)}`,
      })),
    };
  }

  if (section === 'heatmap') {
    const heatmapRow = (data.workforce_risk_heatmap || []).find((row) => slugify(row.tenant_name) === itemId);
    if (!heatmapRow) return null;
    return {
      eyebrow: 'Risk Heatmap',
      title: heatmapRow.tenant_name,
      description: 'Heatmap cell detail for cross-tenant workforce risk.',
      metrics: [
        { label: 'Risk', value: heatmapRow.risk },
        { label: 'Stability', value: heatmapRow.stability },
      ],
    };
  }

  if (section === 'alert') {
    const alert = (data.ai_anomaly_tracker || []).find((row) => String(row.id) === itemId);
    if (!alert) return null;
    return {
      eyebrow: 'AI Alert',
      title: alert.title,
      description: alert.description,
      metrics: [
        { label: 'Severity', value: alert.severity },
        { label: 'Risk', value: Number(alert.risk_score || 0).toFixed(1) },
        { label: 'Status', value: String(alert.status || '').replace('_', ' ') },
      ],
      details: [
        { label: 'Tenant', value: alert.tenant_name },
      ],
    };
  }

  if (section === 'report') {
    const report = (data.ai_executive_reports || []).find((row) => slugify(row.title) === itemId);
    if (!report) return null;
    return {
      eyebrow: 'Executive AI Report',
      title: report.title,
      description: report.summary,
      metrics: [{ label: 'Tone', value: report.tone }],
    };
  }

  if (section === 'predictive') {
    const predictiveMap = {
      'attrition-forecasting': {
        title: 'Attrition Forecasting',
        description: 'Cross-tenant modeled attrition trend.',
        suffix: '%',
        series: data.predictive_analytics?.attrition_forecasting || [],
      },
      'workforce-forecasting': {
        title: 'Workforce Forecasting',
        description: 'Expected active workforce trajectory.',
        series: data.predictive_analytics?.workforce_forecasting || [],
      },
      'payroll-forecasting': {
        title: 'Payroll Forecasting',
        description: 'Projected payroll movement.',
        series: data.predictive_analytics?.payroll_forecasting || [],
        formatter: formatINR,
      },
    };
    const predictive = predictiveMap[itemId];
    if (!predictive) return null;
    const latest = predictive.series[predictive.series.length - 1]?.value ?? 0;
    return {
      eyebrow: 'Predictive Analytics',
      title: predictive.title,
      description: predictive.description,
      metrics: [
        { label: 'Latest Forecast', value: predictive.formatter ? predictive.formatter(latest) : `${latest}${predictive.suffix || ''}` },
        { label: 'Data Points', value: predictive.series.length },
      ],
      related: detailCardItems(predictive.series, (row, index) => ({
        key: `${itemId}-${index}`,
        title: row.label,
        subtitle: 'Forecast point',
        meta: predictive.formatter ? predictive.formatter(row.value) : `${row.value}${predictive.suffix || ''}`,
      })),
    };
  }

  if (section === 'model-health') {
    const model = modelHealth.find((row) => slugify(row.name) === itemId);
    if (!model) return null;
    return {
      eyebrow: 'Model Health',
      title: model.name,
      description: 'AI model performance and operational health.',
      metrics: [
        { label: 'Status', value: model.status },
        { label: 'Latency', value: `${model.latency_ms} ms` },
        { label: 'Confidence', value: `${Math.round((model.confidence || 0) * 100)}%` },
        { label: 'Throughput', value: model.throughput },
      ],
    };
  }

  if (section === 'recommendation') {
    const recommendation = recommendationApprovals.find((row) => String(row.id) === itemId);
    if (!recommendation) return null;
    return {
      eyebrow: 'AI Recommendation',
      title: recommendation.title,
      description: recommendation.rationale || recommendation.summary,
      metrics: [
        { label: 'Status', value: String(recommendation.status || '').replace('_', ' ') },
        { label: 'Confidence', value: `${recommendation.confidence_score || 0}%` },
        { label: 'Impact', value: Number(recommendation.impact_score || 0).toFixed(1) },
      ],
    };
  }

  if (section === 'activity') {
    const activity = promptActivityLogs.find((row) => String(row.id) === itemId);
    if (!activity) return null;
    return {
      eyebrow: 'AI Activity Event',
      title: activity.prompt_text,
      description: `${activity.intent} executed by ${activity.actor_role}.`,
      metrics: [
        { label: 'Latency', value: `${activity.latency_ms} ms` },
        { label: 'Confidence', value: `${activity.confidence_score}%` },
      ],
      details: [
        { label: 'Intent', value: activity.intent },
        { label: 'Actor Role', value: activity.actor_role },
        { label: 'Created At', value: activity.created_at ? new Date(activity.created_at).toLocaleString() : '--' },
      ],
    };
  }

  if (section === 'service') {
    const service = microservices.find((row) => slugify(row.name) === itemId);
    if (!service) return null;
    return {
      eyebrow: 'AI Service',
      title: service.name,
      description: service.purpose,
      metrics: [
        { label: 'Status', value: service.status },
        { label: 'Runtime', value: service.runtime },
        { label: 'Mode', value: service.mode },
      ],
    };
  }

  return null;
};

const SuperadminAICommandDetailPage = ({ activeTab, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    summary_strip: [],
    global_ai_insights: [],
    tenant_health_matrix: [],
    workforce_risk_heatmap: [],
    ai_anomaly_tracker: [],
    predictive_analytics: {},
    ai_governance_center: {},
    ai_executive_reports: [],
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await aiCommandAPI.getSuperadminCommandCenter();
      setData(response.data || {});
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load AI detail page'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const detail = useMemo(() => buildDetail(data, activeTab), [activeTab, data]);

  if (loading) {
    return (
      <div className="space-y-6">
        <StatSkeletonGrid count={4} />
      </div>
    );
  }

  if (!detail) {
    return (
      <Card className={LIGHT_PANEL}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <BrainCircuit className="h-4 w-4 text-primary" />
            Detail not found
          </CardTitle>
          <CardDescription className="text-muted-foreground">This AI Dashboard detail page is unavailable.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => onNavigate('ai-command')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to AI Dashboard
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 text-foreground">
      <section className="group relative overflow-hidden rounded-[28px] border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-info/10 p-6 shadow-sm">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-16 top-0 h-40 w-40 rounded-full bg-info/10 blur-3xl transition duration-700 group-hover:scale-110" />
          <div className="absolute right-0 top-8 h-44 w-44 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
        </div>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-3 py-1 text-xs font-medium text-primary shadow-sm backdrop-blur-sm">
              <BrainCircuit className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
              {detail.eyebrow || 'AI Dashboard Detail'}
            </div>
            <div>
              <h1 className="text-hr-h1 font-bold tracking-tight text-foreground">{detail.title}</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">{detail.description}</p>
            </div>
          </div>
          <Button variant="outline" className="border-border bg-card/92 text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/20 hover:bg-muted hover:text-primary" onClick={() => onNavigate('ai-command')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to AI Dashboard
          </Button>
        </div>
      </section>

      {(detail.metrics || []).length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {detail.metrics.map((metric) => (
            <Card key={metric.label} className={LIGHT_PANEL}>
              <CardContent className="p-5">
                <p className={LIGHT_LABEL}>{metric.label}</p>
                <p className="mt-3 text-2xl font-semibold text-foreground">{metric.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(detail.details || []).length > 0 && (
        <Card className={LIGHT_PANEL}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Detail Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {detail.details.map((item) => (
              <div key={item.label} className={`${LIGHT_SUBPANEL} p-4`}>
                <p className={LIGHT_LABEL}>{item.label}</p>
                <p className="mt-2 text-sm font-medium text-foreground break-words">{formatValue(item.value)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(detail.related || []).length > 0 && (
        <Card className={LIGHT_PANEL}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Radar className="h-4 w-4 text-primary" />
              Detailed Items
            </CardTitle>
            <CardDescription className="text-muted-foreground">Expanded records for this AI Dashboard surface.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 xl:grid-cols-2">
            {detail.related.map((item) => (
              <div key={item.key} className="rounded-2xl border border-border bg-muted p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{item.subtitle}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-2 text-primary shadow-sm">
                    {String(item.meta || '').toLowerCase().includes('service') ? <Cpu className="h-4 w-4" /> : String(item.meta || '').toLowerCase().includes('confidence') ? <Sparkles className="h-4 w-4" /> : String(item.meta || '').toLowerCase().includes('latency') ? <Activity className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                  </div>
                </div>
                <p className="mt-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">{item.meta}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SuperadminAICommandDetailPage;
